import React, { useState, useRef, useEffect } from "react";
import {
  XMarkIcon,
  ChevronRightIcon,
  CheckIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { getMemberList, createGroup, getChatList, unSetSelectedChat, setSelectedChat } from "@/store/slices/chatSlice";
import { PhotoIcon, UsersIcon } from "@heroicons/react/24/solid";
import Toaster from "@/components/Toaster";
import { useTranslation } from "react-i18next";

const CreateGroupFlow = ({ onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('chat');
  const { member_list, member_list_pagination, member_list_loading, create_group_loading } = useSelector(
    (state) => state.chat
  );
  const hasMore = member_list_pagination?.has_more ?? false;

  const [step, setStep] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [groupData, setGroupData] = useState({
    name: "",
    description: "",
    image: null,
    imagePreview: null,
  });

  const fileInputRef = useRef(null);

  // Load members on mount or search change
  useEffect(() => {
    setPage(1);
    dispatch(getMemberList({ page: 1, search, reset: true })); // reset old members
  }, [dispatch, search]);

  const fetchMoreMembers = () => {
    if (!hasMore || member_list_loading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    dispatch(getMemberList({ page: nextPage, search }));
  };

  const toggleMember = (id) => {
    setSelectedMembers((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  // const handleImageChange = (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;
  //   setGroupData((prev) => ({
  //     ...prev,
  //     image: file,
  //     imagePreview: URL.createObjectURL(file),
  //   }));
  // };

  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "image/webp",
  ];

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type) {
      Toaster.error(t('foldersNotSupported'));
      e.target.value = "";
      return;
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      Toaster.error(t('imageOnly'));
      e.target.value = "";
      return;
    }

    setGroupData((prev) => ({
      ...prev,
      image: file,
      imagePreview: URL.createObjectURL(file),
    }));
  };


  const handleFinalSubmit = async () => {
    if (!groupData.name || create_group_loading) return;

    const formData = new FormData();
    formData.append("name", groupData.name);
    formData.append("description", groupData.description || "");
    if (groupData.image) formData.append("image", groupData.image);
    selectedMembers.forEach((id) => formData.append("member_ids[]", id));
    const result = await dispatch(createGroup(formData));

    if (createGroup.fulfilled.match(result)) {
      onSuccess?.(result.payload);
      dispatch(getChatList({ page: 1 })).unwrap().then((res) => {
        dispatch(setSelectedChat(res?.chatList[0]));
      });
      
    }
  };

  return (
    <div className="flex flex-col h-full bg-white relative">

      {/* Header */}
      <div className="p-4 flex items-center gap-2">
        {/* <button onClick={onCancel}>
          <XMarkIcon className="h-6 w-6 text-[#333333DE]" />
        </button> */}

        {step === 1 && <div className="relative flex-grow">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#333333DE]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('searchMembers')}
            className="w-full bg-[#0000000A] rounded-full py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-1 focus:ring-[#00a884] placeholder-[#736C64]"
          />
          {search && <XMarkIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#333333DE] cursor-pointer" onClick={() => setSearch('')} />}
          
        </div>}
      </div>

      {/* Step 1: Select members */}
      {step === 1 && (
        <div id="membersScroll" className="flex-grow overflow-y-auto px-4 pb-24">
          <InfiniteScroll
            dataLength={member_list?.length}
            next={fetchMoreMembers}
            hasMore={hasMore}
            scrollableTarget="membersScroll"
            loader={<div className="flex justify-center mb-4">
              <div className="h-6 w-6 border-2 border-gray-400 border-t-[#065C36] rounded-full animate-spin"></div>
            </div>}
            endMessage={
              <p className="text-center text-gray-400 py-6 text-sm">
                {member_list?.length === 0 ? t('noUserFound') : ''}
              </p>
            }
          >
            {member_list.map((u) => (
              <div
                key={u.user_id}
                onClick={() => toggleMember(u.user_id)}
                className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-xl cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={u.profile_image || "/img/icons/user-profile.png"}
                    className="w-11 h-11 rounded-full object-cover"
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">
                      {u.full_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {u.user_role}
                    </span>
                  </div>
                </div>
                
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedMembers.includes(u.user_id)
                      ? "bg-[#065C36] border-[#065C36]"
                      : "border-gray-300"
                  }`}
                >
                  {selectedMembers.includes(u.user_id) && (
                    <CheckIcon className="h-3 w-3 text-white stroke-[3]" />
                  )}
                </div>
              </div>
            ))}
          </InfiniteScroll>
        </div>
      )}

      {/* Step 2: Group details */}
      {step === 2 && (
        <div className="flex-grow text-center w-full p-5">
          {/* Group Icon Section */}
          <div
            className="relative w-28 h-28 mx-auto mb-10 rounded-full bg-[#9E9E9E] flex items-center justify-center overflow-hidden cursor-pointer group"
            onClick={() => fileInputRef.current.click()}
          >
            {/* 1. The Background Placeholder (UsersIcon) */}
            {!groupData.imagePreview && (
              <UsersIcon className="h-14 w-14 text-black opacity-30" />
            )}

            {/* 2. The Uploaded Image (if exists) */}
            {groupData.imagePreview && (
              <img src={groupData.imagePreview} className="w-full h-full object-cover" alt="Preview" />
            )}

            {/* 3. The Top Level Transparent Overlay (Icon + Text) */}
            <div className="absolute inset-0 bg-[black/20] flex flex-col items-center justify-center text-white transition-colors group-hover:bg-black/40">
              <PhotoIcon className="h-6 w-6 mb-1" />
              <span className="text-[10px] font-semibold leading-tight tracking-wide text-center px-2">
                {t('addGroupIcon').split('<1/>')[0]} <br /> {t('addGroupIcon').split('<1/>')[1]}
              </span>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/*"
            onChange={handleImageChange}
          />

          {/* Input Fields */}
          <div className="space-y-4 text-left">
            <div className="relative">
              <input
                placeholder={t('groupName')}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                value={groupData.name}
                maxLength={25}
                onChange={(e) => setGroupData({ ...groupData, name: e.target.value })}
              />
            </div>

            <textarea
              maxLength={100}
              placeholder={t('descriptionOptional')}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none transition-all"
              value={groupData.description}
              onChange={(e) => setGroupData({ ...groupData, description: e.target.value })}
            />
          </div>
        </div>
      )}

      {/* Footer Button */}
      <div className="absolute bottom-6 right-6">
        {step === 1 ? (
          <button
            disabled={!selectedMembers.length}
            onClick={() => setStep(2)}
            className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              selectedMembers?.length ? "bg-[#065C36] text-white" : "bg-gray-200 text-gray-400"
            }`}
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>
        ) : (
          // <button
          //   disabled={!groupData.name || create_group_loading}
          //   onClick={handleFinalSubmit}
          //   className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          //     groupData.name ? "bg-[#065C36] text-white" : "bg-gray-200 text-gray-400"
          //   }`}
          // >
          //   <CheckIcon className="h-6 w-6" />
          // </button>
          <button
            disabled={!groupData.name.trim() || !groupData.description.trim() || create_group_loading}
            onClick={handleFinalSubmit}
            className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            groupData.name.trim() && groupData.description.trim()
            ? "bg-[#065C36] text-white cursor-pointer"
            : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
            >
            <CheckIcon className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateGroupFlow;
